import React, { useMemo } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import SvgFormat from '~helpers/svgFormat';
import Countdown from '~components/countdown';
import useDueHours from '~components/countdown/useDueHours';

import normalProcesses from './normalProcesses';
import nopaymentProcesses from './nopaymentProcesses';
import { orderStage, orderSecondaryStage } from '~constants/bookings';

const BookingProcessBar = ({ booking }) => {
  const { t } = useTranslation();
  const {
    processes,
    bookingJourney,
    stage,
    secondaryStage,
    confirmationDuration,
    paymentDuration,
    counterOffers,
    paymentOnline,
    instalmentPlanType,
  } = booking;
  const counterOfferPeriodDurations = counterOffers?.[0]?.validPeriodHour;
  const isNoPaymentReselectPaymentPlan =
    (stage === orderStage.WAITING_FOR_PAYMENT &&
      secondaryStage === orderSecondaryStage.COUNTER_OFFER_ACCEPTED_BY_STUDENT &&
      !paymentOnline &&
      !instalmentPlanType) ||
    (!paymentOnline &&
      processes?.filter(
        item =>
          item?.currentStage === orderStage.WAITING_FOR_PAYMENT &&
          item?.currentSecondaryStage === orderSecondaryStage.COUNTER_OFFER_ACCEPTED_BY_STUDENT,
      )?.length > 0);

  const { milestoneDetails, milestoneStatus } = useMemo(
    () =>
      paymentOnline
        ? normalProcesses({
            stage,
            processes,
            bookingJourney,
            isPaymentOnline: paymentOnline,
            isNoPaymentReselectPaymentPlan,
          })
        : nopaymentProcesses(booking),
    [paymentOnline],
  );
  const { dueHours } = useDueHours({
    stage,
    secondaryStage,
    confirmationDuration,
    paymentDuration,
    counterOfferPeriodDurations,
  });

  const hiddenMakingPaymentStage = !paymentOnline;
  return (
    <div className={classNames('longtail-process-bar')}>
      <div className="longtail-process-bar__horizontal-line-container" />
      <ul className="longtail-process-bar__process-container">
        <For of={Object.keys(milestoneDetails)} index="idx" each="key">
          <li
            className={classNames(
              'longtail-process-bar__item',
              `longtail-process-bar__item--${milestoneDetails[key].status}`,
            )}
            style={{ width: hiddenMakingPaymentStage ? '25%' : '20%' }}
            key={key}
          >
            <div className="longtail-process-bar__icon-bg">
              <div
                className={classNames(
                  'longtail-process-bar__icon-container',
                  `longtail-process-bar__icon-container--${milestoneDetails[key].status}`,
                )}
              >
                <SvgFormat
                  name="student-portal"
                  type={milestoneDetails[key].iconHash}
                  className={`longtail-process-bar__icon longtail-process-bar__icon--${key.toLowerCase()}`}
                />
              </div>
            </div>

            <div className="longtail-process-bar__item-label-container">
              <p className="longtail-process-bar__item-label">
                {t(`landlord.longtail_booking.process.title.${key}`)}
              </p>

              <If condition={milestoneDetails[key]?.secondaryStage === 'pending_counter_offer'}>
                <p className="instant-process-bar__item-label--stage">
                  {t(
                    `landlord.longtail_booking.process.title.${milestoneDetails[key]?.secondaryStage}`,
                  )}
                  {}
                </p>
              </If>

              {/* <If condition={milestoneDetails[key] && milestoneDetails[key].modifierName}>
                <p className="longtail-process-bar__item-label--modifier">
                  {milestoneDetails[key].modifierName}
                </p>
              </If> */}

              <If
                condition={
                  milestoneDetails[key] &&
                  milestoneDetails[key].updatedAt &&
                  (milestoneDetails[key].status === milestoneStatus.DONE ||
                    milestoneDetails[key].status === milestoneStatus.FAILED)
                }
              >
                <p className="longtail-process-bar__item-label--time">
                  {moment(milestoneDetails[key].updatedAt).format('YYYY-MM-DD HH:mm:ss')}
                </p>
              </If>

              <If condition={milestoneDetails[key]?.countdown}>
                <Countdown
                  className="longtail-process-bar__countdown"
                  initTime={milestoneDetails[key].countdown.initTime}
                  t={t}
                  tooltip={
                    milestoneDetails[key]?.secondaryStage ===
                    orderSecondaryStage.PENDING_COUNTER_OFFER.toLowerCase()
                      ? null
                      : t(`landlord.longtail_booking.process.tooltip.${key}`, {
                          dueHours,
                        })
                  }
                  dueHours={dueHours}
                />
              </If>
            </div>
          </li>
        </For>
      </ul>
    </div>
  );
};

BookingProcessBar.propTypes = {
  booking: PropTypes.object,
};

BookingProcessBar.defaultProps = {
  booking: {},
};

export default BookingProcessBar;
